.compactField {
  margin-top: 0;
}

.welcomeContent {
  align-items: center;
  display: flex;
  gap: 0.75rem;
}

.welcomeText {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

/* From 1000px: the lists and the card in 8 columns, the recommendation and the year in 4.
   The grid only zeroes margins on its direct children, so the columns do it for theirs. */
@media (min-width: 1000px) {
  .mainColumn,
  .sideColumn {
    display: flex;
    flex-direction: column;
    gap: var(--section-margin);
  }

  .mainColumn {
    grid-column: span 8;
  }

  .sideColumn {
    grid-column: span 4;
  }

  .mainColumn > *,
  .sideColumn > * {
    margin: 0;
  }
}

.hero {
  background: linear-gradient(135deg, var(--color-primary-light), var(--color-primary) 55%, var(--color-primary-deep));
  border-radius: 16px;
  color: var(--color-text-on-primary);
  height: 100%;
  overflow: hidden;
  position: relative;
}

.heroContent {
  align-items: center;
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  padding: 32px;
  position: relative;
  z-index: 1;
}

.heroContent:has(> :only-child) {
  grid-template-columns: minmax(0, 1fr);
}

@media (max-width: 1279px) {
  .heroContent {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 480px) {
  .heroContent {
    padding: 20px;
  }
}

.heroText {
  isolation: isolate;
  position: relative;
}

.heroText p {
  margin: 0 0 1rem;
}

.heroText p:not(.eyebrow) {
  color: var(--color-text-on-primary-soft);
}

.eyebrow {
  color: var(--color-accent);
  font-size: var(--font-size-small);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.heroTitle {
  font-family: var(--font-family-heading);
  font-size: 2rem;
  line-height: 1.15;
  margin: 0 0 0.75rem;
}

.heroCard {
  min-width: 0;
}

.heroActions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.primaryAction,
.secondaryAction {
  border-radius: 999px;
  cursor: pointer;
  font: inherit;
  font-size: var(--font-size-small);
  font-weight: 400;
  padding: 10px 18px;
  text-decoration: none;
}

.primaryAction {
  background: var(--color-cta);
  border: 0;
  color: var(--color-primary);
}

.secondaryAction {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: var(--color-text-on-primary);
}

.primaryAction:focus-visible,
.secondaryAction:focus-visible,
.surveyRow:focus-visible,
.goal:focus-visible,
.goalFinished:focus-visible {
  outline: 3px solid var(--color-accent);
  outline-offset: 2px;
}

.panelTitle {
  font-family: var(--font-family-heading);
  font-size: 1.5rem;
  margin: 0;
}

.stats {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 1rem 0;
}

.stats dt {
  color: var(--color-text-muted);
  font-size: var(--font-size-small);
}

.stats dd {
  font-family: var(--font-family-heading);
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}

.alerts {
  border-top: 1px solid var(--color-muted);
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
  margin: 0;
  padding: 1rem 0 0;
}

.listHeading {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
}

.surveyRow {
  align-items: center;
  background: #fff;
  border-radius: 12px;
  color: inherit;
  display: grid;
  gap: 16px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 16px;
}

.surveyInfo {
  min-width: 0;
}

.rowTitle,
.goalTitle {
  font-size: 1.1rem;
  margin: 0 0 0.25rem;
  overflow-wrap: anywhere;
}

.rowTitle {
  font-weight: 400;
}

.goalTitle {
  font-family: var(--font-family-heading);
}

.rowMeta {
  color: var(--color-text-muted);
  display: flex;
  flex-wrap: wrap;
  font-size: var(--font-size-small);
  gap: 4px 12px;
  margin: 0;
}

.tag {
  background: var(--color-tertiary-background);
  border-radius: 999px;
  color: var(--color-text);
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 2px 10px;
  width: fit-content;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}

.chip,
.chipLowest {
  border-radius: 6px;
  font-size: 0.8rem;
  padding: 2px 8px;
}

.chip {
  background: var(--color-muted);
}

.chipLowest {
  background: rgba(240, 138, 108, 0.2);
  color: #8a3a22;
  font-weight: 600;
}

.goal,
.goalFinished {
  background: #fff;
  border-radius: 12px;
  color: inherit;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
}

.goalFinished {
  opacity: 0.7;
}

.journeyLine svg {
  display: block;
  height: 16px;
  width: 100%;
}

.phases {
  color: var(--color-text-muted);
  display: flex;
  font-size: 0.75rem;
  justify-content: space-between;
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}

.phaseActive {
  color: var(--color-text);
  font-weight: 700;
}
